// src/hooks/useUser.ts

import { useCallback, useState } from 'react'
import userApiService from '../api/user.api'
import { User } from 'domain/user'
import { useToast } from 'layout/context/toast-context'
import { AxiosError } from 'axios'
import { ApiResponse } from 'http/types/api-response'
import { ApiErrorResponse } from 'http/types/api-error-response'
import {
  UserQueryParams,
  CreateUserDto,
  UpdateUserDto,
} from '../types/user.types'

interface UseUserHookReturn {
  // List operations
  users: User[]
  totalUsers: number
  isLoading: boolean
  error: Error | null

  // Actions
  fetchUsers: (params?: UserQueryParams) => Promise<void>
  getUser: (id: string) => Promise<User | null>
  createUser: (data: CreateUserDto, clientId: string) => Promise<User | null>
  updateUser: (id: string, data: UpdateUserDto) => Promise<User | null>
  deleteUser: (id: string) => Promise<boolean>
  toggleActive: (id: string, active: boolean) => Promise<User | null>
  toggleBlocked: (id: string, blocked: boolean) => Promise<User | null>
}

export const useUser = (): UseUserHookReturn => {
  const { showSuccess, showError } = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [totalUsers, setTotalUsers] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchUsers = useCallback(
    async (params?: UserQueryParams): Promise<void> => {
      // Only proceed if params has clientId
      if (!params?.clientId) {
        return
      }

      setIsLoading(true)
      setError(null)

      await userApiService.getUsers(params)
        .then((response) => {
          setUsers(response.responseObject)
          setTotalUsers(response.responseObject.length)
        })
        .catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          setUsers([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [showError]
  )

  /**
   * Get a single user by ID
   */
  const getUser = useCallback(async (id: string): Promise<User | null> => {
    setIsLoading(true)
    const response = await userApiService.getUserById(id)
      .then((data: ApiResponse<User>) => {
        return data.responseObject
      })
      .catch((error: AxiosError) => {
        const err = error.response?.data as ApiErrorResponse

        if (err.error && err.message) {
          showError(err.error, err.message)
        }
        setError(error as Error)
        return null
      })
      .finally(() => {
        setIsLoading(false)
      })

    return response
  }, [showError])

  /**
   * Create a new user
   */
  const createUser = useCallback(
    async (data: CreateUserDto, clientId: string): Promise<User | null> => {

      setIsLoading(true)
      const response = await userApiService.createUser(data, clientId)
        .then((data: ApiResponse<User>) => {
          const newUser = data.responseObject as User
          if (newUser.clientId === clientId) {
            fetchUsers({ clientId })
          }

          showSuccess('Sucesso', `Usuário ${newUser.name} criado com sucesso`)

          return newUser
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [fetchUsers, showSuccess, showError]
  )

  /**
   * Update an existing user
   */
  const updateUser = useCallback(
    async (id: string, data: UpdateUserDto): Promise<User | null> => {

      setIsLoading(true)
      const response = await userApiService.updateUser(id, data)
        .then((data: ApiResponse<User>) => {
          const updatedUser = data.responseObject as User

          // Update the local state to reflect changes
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === id ? updatedUser : user
            )
          )

          showSuccess('Sucesso', `Usuário ${updatedUser.name} atualizado com sucesso`)

          return updatedUser
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [showSuccess, showError]
  )

  /**
   * Delete a user
   */
  const deleteUser = useCallback(async (id: string): Promise<boolean> => {

    setIsLoading(true)
    const response = await userApiService.deleteUser(id)
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== id)
        )

        showSuccess('Sucesso', 'Usuário excluído com sucesso')
        return true
      }).catch((error: AxiosError) => {
        const err = error.response?.data as ApiErrorResponse

        if (err.error && err.message) {
          showError(err.error, err.message)
        }
        setError(error as Error)
        return false
      }).finally(() => {
        setIsLoading(false)
      })

    return response
  }, [showSuccess, showError])

  /**
   * Toggle user active status
   */
  const toggleActive = useCallback(
    async (id: string, active: boolean): Promise<User | null> => {

      setIsLoading(true)
      const response = await userApiService.updateUser(id, {
        id,
        active,
      })
        .then((data: ApiResponse<User>) => {
          const updatedUser = data.responseObject as User

          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === id ? updatedUser : user
            )
          )

          showSuccess('Sucesso', `Usuário ${active ? 'ativado' : 'desativado'} com sucesso`)

          return updatedUser
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [showSuccess, showError]
  )

  /**
   * Toggle user blocked status
   */
  const toggleBlocked = useCallback(
    async (id: string, blocked: boolean): Promise<User | null> => {

      setIsLoading(true)
      const response = await userApiService.updateUser(id, {
        id,
        blocked,
      })
        .then((data: ApiResponse<User>) => {
          const updatedUser = data.responseObject as User

          // Update the local state to reflect changes
          setUsers((prevUsers) =>
            prevUsers.map((user) =>
              user.id === id ? updatedUser : user
            )
          )

          showSuccess('Sucesso', `Usuário ${blocked ? 'bloqueado' : 'desbloqueado'} com sucesso`)

          return updatedUser
        }).catch((error: AxiosError) => {
          const err = error.response?.data as ApiErrorResponse | undefined

          if (err?.error && err?.message) {
            showError(err.error, err.message)
          } else if (!error.response) {
            showError('Erro de conexão', 'Serviço temporariamente indisponível')
          }
          setError(error as Error)
          return null
        }).finally(() => {
          setIsLoading(false)
        })

      return response
    },
    [showSuccess, showError]
  )

  return {
    users,
    totalUsers,
    isLoading,
    error,
    fetchUsers,
    getUser,
    createUser,
    updateUser,
    deleteUser,
    toggleActive,
    toggleBlocked,
  }
}
